import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Play, FileText, Save, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { languages as allLanguages } from "../lib/languages";

export default function Transcript() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { t, changeLanguage } = useLanguage();

  const [caseData, setCaseData] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [editedTranscript, setEditedTranscript] = useState("");
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [caseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [caseRes, transcriptRes] = await Promise.all([
        api.getCase(caseId),
        api.getTranscript(caseId).catch(() => null),
      ]);
      setCaseData(caseRes.data);
      if (transcriptRes) {
        setTranscript(transcriptRes.data);
        setEditedTranscript(transcriptRes.data.transcript_text);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error(t('error_load_transcript'));
    } finally {
      setLoading(false);
    }
  };



  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateTranscript(caseId, editedTranscript);
      toast.success(t('success_transcript_updated'));
      setTranscript(prev => ({ ...prev, transcript_text: editedTranscript }));
    } catch (error) {
      console.error("Failed to save transcript:", error);
      toast.error(t('error_save_transcript'));
    } finally {
      setSaving(false);
    }
  };

  const runAnalysis = async () => {
    try {
      // Find the language object in allLanguages (flattening dialects if necessary)
      let targetLangObj = allLanguages.find(l => l.code === targetLanguage);
      if (!targetLangObj) {
        for (const l of allLanguages) {
          if (l.dialects) {
            const d = l.dialects.find(dial => dial.code === targetLanguage);
            if (d) {
              targetLangObj = d;
              break;
            }
          }
        }
      }

      // Proactively switch app language to target language
      if (targetLangObj) {
        changeLanguage(targetLangObj);
      }

      setAnalyzing(true);
      setShowAnalysisDialog(false);
      await api.analyze(caseId, { target_language: targetLanguage });
      toast.success(t('success_analysis_complete'));
      navigate(`/case/${caseId}/results`);
    } catch (error) {
      console.error("Failed to analyze:", error);
      toast.error(error.response?.data?.detail || t('error_analysis_failed'));
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/case/${caseId}`} data-testid="back-to-case">
                <Button variant="ghost" size="sm" className="rounded-sm" disabled={analyzing}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="transcript-title">
                  {t('review_edit_transcript')}
                </h1>
                <p className="text-sm text-slate-500">{caseData?.participant_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={!transcript || saving || (transcript.transcript_text === editedTranscript)}
                className="rounded-sm"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t('save_changes')}
              </Button>
              <div className="flex items-center gap-2 border-l border-slate-200 pl-2">
                <Button
                  onClick={() => setShowAnalysisDialog(true)}
                  disabled={!transcript || analyzing || (transcript.transcript_text !== editedTranscript)}
                  className="bg-slate-700 hover:bg-slate-800 rounded-sm px-6"
                  data-testid="run-analysis-btn"
                  title={transcript && transcript.transcript_text !== editedTranscript ? t('save_before_analyzing') : t('run_analysis_tooltip')}
                >
                  {analyzing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      {t('analyzing_status')}
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      {t('run_matrix_analysis')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {!transcript ? (
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">{t('no_transcript_title')}</h3>
              <p className="text-slate-500 mb-6">{t('no_transcript_desc')}</p>
              <Link to={`/case/${caseId}/record`}>
                <Button className="bg-slate-700 hover:bg-slate-800 rounded-sm">
                  {t('go_to_recording')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-['Manrope']">{t('edit_transcript_title')}</CardTitle>
                  <CardDescription>
                    {t('edit_transcript_desc')}
                  </CardDescription>
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  SHA256: {transcript.sha256?.substring(0, 12)}...
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="p-0 relative">
                <div className="absolute top-2 right-2 text-xs text-slate-400 bg-white/80 px-2 py-1 rounded pointer-events-none">
                  {t('editable_text_label')}
                </div>
                <Textarea
                  value={editedTranscript}
                  onChange={(e) => setEditedTranscript(e.target.value)}
                  className="min-h-[600px] border-0 focus-visible:ring-0 resize-none p-6 font-sans text-base leading-relaxed text-slate-700"
                  placeholder={t('transcript_placeholder')}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analysis Info */}
        {transcript && (
          <div className="mt-6 p-4 bg-slate-100 rounded-sm border border-slate-200">
            <h4 className="font-medium text-slate-800 mb-2">{t('about_matrix_analysis')}</h4>
            <p className="text-sm text-slate-600">
              {t('matrix_analysis_desc')}
            </p>
          </div>
        )}
      </main>

      {/* Analysis Loading Overlay */}
      {analyzing && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">{t('analyzing_transcript_title')}</h3>
              <p className="text-sm text-slate-600 mb-4">
                {t('analyzing_transcript_desc')}
              </p>
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                <p className="text-xs text-slate-500 mb-2">{t('estimated_time')}</p>
                <p className="text-lg font-semibold text-slate-700">{t('time_estimate')}</p>
              </div>
              <p className="text-xs text-slate-500 mt-4 italic">
                {t('do_not_close')}
              </p>
            </div>
          </Card>
        </div>
      )}
      {/* Analysis Configuration Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Manrope']">
              {t('analysis_config_title')}
            </DialogTitle>
            <DialogDescription>
              {t('analysis_config_desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              {t('target_language_label')}
            </label>
            <Select value={targetLanguage} onValueChange={setTargetLanguage}>
              <SelectTrigger className="w-full h-11 rounded-sm border-slate-200">
                <SelectValue placeholder={t('select_language_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {allLanguages.map(lang => {
                  if (lang.dialects) {
                    return lang.dialects.map(d => (
                      <SelectItem key={d.code} value={d.code}>
                        {lang.name} ({d.name})
                      </SelectItem>
                    ));
                  }
                  return (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2 italic">
              {t('language_analysis_note')}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalysisDialog(false)} className="rounded-sm">
              {t('cancel')}
            </Button>
            <Button
              onClick={runAnalysis}
              className="bg-slate-800 hover:bg-slate-900 rounded-sm px-8"
            >
              {t('start_analysis_btn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
